import { SelectInputData } from "@src/components/deprecated/SelectInputField/SelectInputField";
import { SelectInput } from "@src/components/ui-components/Selectors/SelectInput/SelectInput";
import { useEffect, useState } from "react";
import { DataObjectFieldComparator } from "../../DataObjects/DataObjectFieldComparator";
import { DataObjectType } from "../../DataObjects/DataObjectHelper";
import { DataObjectField, InputElementContext } from "../DataObjectField";
import styles from "../DataObjectField.module.css";

export type FirebaseDataSelectFieldOption = { key: string; label: string };
export abstract class FirebaseDataSelectField<FieldType, Type extends DataObjectType> extends DataObjectField<FieldType, Type> {
  abstract getValueForKey(key: string): FieldType | undefined;
  abstract getOptions(context?: InputElementContext): FirebaseDataSelectFieldOption[];
  protected abstract convertValueToSelectInput(value: FieldType, context?: InputElementContext): SelectInputData;
  hasCustomOptionOrder = false;

  getSelectInput(value: any, context: InputElementContext | undefined): SelectInputData | undefined {
    const decodedValue = this.getValue(value);
    return decodedValue !== null && decodedValue !== undefined ? this.convertValueToSelectInput(decodedValue, context) : undefined;
  }

  override singleInputElement = (
    value: FieldType | undefined,
    setValue: (value: FieldType | undefined) => void,
    context?: InputElementContext,
    useCustomSort?: boolean
  ) => {
    return <DataObjectSelectFieldSingleInputElement field={this} value={value} setValue={setValue} context={context} useCustomSort={useCustomSort} />;
  };
  override multiInputElement = (
    values: FieldType[] | undefined,
    setValue: (value: FieldType[] | undefined) => void,
    context?: InputElementContext,
    useCustomSort?: boolean,
    options?: SelectInputData[]
  ) => {
    return (
      <DataObjectSelectFieldMultiInputElement
        field={this}
        value={values}
        setValue={setValue}
        context={context}
        useCustomSort={useCustomSort}
        options={options}
      />
    );
  };

  override getReadableStringRepresentation(value: FieldType): string {
    return "Error";
  }
  override encodeString = (value: FieldType) => this.getSelectInput(value, undefined)?.value ?? "";
  override decodeString = (value: string) => this.getValueForKey(value);
  decodeStringAsRawValue = () => false;
  override decode = (value: any): FieldType | undefined => {
    if (this.isValueValid(value)) return value;
    return typeof value === "string" ? this.decodeString(value) : value !== undefined ? this.decodeString(value + "") : undefined;
  };

  override allComparators: DataObjectFieldComparator[] = [
    "equals",
    "not-equals",
    "is-one-of",
    "is-not-one-of",
    "contains-all",
    "is-known",
    "is-unknown",
  ];

  loadAllDatas(context?: InputElementContext): Promise<any> {
    return Promise.resolve();
  }

  override equalComparator = (a: FieldType | undefined, b: FieldType | undefined) => {
    return this.getSelectInput(a, undefined)?.value === this.getSelectInput(b, undefined)?.value;
  };
}

const DataObjectSelectFieldSingleInputElement = <FieldType, Type extends DataObjectType>(props: {
  field: FirebaseDataSelectField<FieldType, Type>;
  value: FieldType | undefined;
  setValue: (value: FieldType | undefined) => void;
  context?: InputElementContext;
  useCustomSort?: boolean;
}) => {
  const [, setLastUpdate] = useState(new Date());
  const selectInput = props.field.getSelectInput(props.value, props.context);

  useEffect(() => {
    props.field.loadAllDatas(props.context).then(() => {
      setLastUpdate(new Date());
    });
  }, [props.context, props.field]);

  return (
    <SelectInput
      isMulti={false}
      value={selectInput?.value}
      onChange={(data) => {
        if (data) {
          props.setValue(props.field.getValueForKey(data));
        } else {
          props.setValue(undefined);
        }
      }}
      useCustomSort={props.useCustomSort ?? props.field.hasCustomOptionOrder}
      options={props.field.getOptions(props.context).map((option) => new SelectInputData(option.key, option.label))}
      width={props.context?.width ?? props.context?.minWidth ?? "100%"}
      height={props.context?.height}
      className={styles.selectInput}
      isDisabled={props.context?.disabled}
      dataCy={props.context?.dataCy}
    />
  );
};

const DataObjectSelectFieldMultiInputElement = <FieldType, Type extends DataObjectType>(props: {
  field: FirebaseDataSelectField<FieldType, Type>;
  value: FieldType[] | undefined;
  setValue: (value: FieldType[] | undefined) => void;
  context?: InputElementContext;
  useCustomSort?: boolean;
  options?: SelectInputData[];
}) => {
  const [, setLastUpdate] = useState(new Date());
  let selectedValues: string[] = [];
  if (props.value && Array.isArray(props.value)) {
    selectedValues = props.value.map((value) => props.field.getSelectInput(value, props.context)?.value ?? "");
  }

  useEffect(() => {
    props.field.loadAllDatas(props.context).then(() => {
      setLastUpdate(new Date());
    });
  }, [props.context, props.field]);

  return (
    <SelectInput
      isMulti
      onChange={(datas) => {
        if (datas.length) {
          const newValues: FieldType[] = [];
          for (const data of datas) {
            const value = props.field.getValueForKey(data);
            if (value) newValues.push(value);
          }
          props.setValue(newValues);
        } else {
          props.setValue(undefined);
        }
      }}
      useCustomSort={props.useCustomSort ?? props.field.hasCustomOptionOrder}
      value={selectedValues}
      options={props.options ?? props.field.getOptions(props.context).map((option) => new SelectInputData(option.key, option.label))}
      width={props.context?.width ?? props.context?.minWidth ?? "100%"}
      height={props.context?.height}
      className={styles.selectInput}
      isDisabled={props.context?.disabled}
      dataCy={props.context?.dataCy}
    />
  );
};
